import { RedirectWithSearch } from '@/components/RedirectWithSearch';
import { FF_ROUTE_PRESCREENING_PATIENT_TO_D2D } from '@/constants/featureFlags';
import { useD2DTracking } from '@/hooks/useD2DTracking';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Where each consult flow starts. Mapped rather than used as a path directly,
 * so the query parameter cannot redirect a patient off-site.
 */
const FLOW_START = {
  d2d: '/triage',
  inc: '/consult-eligible-nurse',
} as const;

/**
 * Entry point the pre-screening site redirects patients to.
 *
 * Pre-screening reports whether a patient is *eligible* for the doctor flow as
 * `consultFlow`; the rollout below decides whether an eligible patient actually
 * takes it.
 *
 * Anything else - a bookmark, a hand-edited URL, an older pre-screening build -
 * falls back to the nurse flow.
 */
export default function ConsultEligible() {
  const [searchParams] = useSearchParams();
  const consultFlow = searchParams.get('consultFlow');
  const { flags, loading } = useFeatureFlags();
  const { trackExperimentImpression } = useD2DTracking();

  const isEligible = consultFlow === 'd2d';
  const isInRollout = !!flags[FF_ROUTE_PRESCREENING_PATIENT_TO_D2D];

  useEffect(() => {
    // Only eligible patients are randomised, so only they belong in the
    // experiment. Everyone the API already ruled out would otherwise inflate
    // the control arm with patients who could never have reached the doctor.
    if (loading || !isEligible) {
      return;
    }

    trackExperimentImpression(isInRollout ? 'variant' : 'control');
  }, [loading, isEligible, isInRollout, trackExperimentImpression]);

  // Redirecting before the flag resolves would send every eligible patient to
  // the default, so hold the screen until there is a real assignment.
  if (isEligible && loading) {
    return null;
  }

  // Only an eligible patient inside the rollout reaches the doctor flow, so an
  // unrecognised `consultFlow` lands on the nurse flow without a separate guard.
  return <RedirectWithSearch to={isEligible && isInRollout ? FLOW_START.d2d : FLOW_START.inc} replace />;
}
